/**
 * Core tracking module — initializes on app mount.
 *
 * Responsibilities:
 * 1. Generate or load visitor fingerprint
 * 2. Load or create visitor ID (from localStorage)
 * 3. Extract UTM parameters (first-touch attribution)
 * 4. Queue events and send in batches
 * 5. Start heartbeat interval (every 15 seconds)
 * 6. Track page visibility changes
 * 7. Track scroll depth on page leave
 * 8. Forward events to backend API
 */

import { generateFingerprint } from "./fingerprint";
import { getEffectiveUTM } from "./utm";

const VISITOR_ID_KEY = "yayy_visitor_id";
const SESSION_ID_KEY = "yayy_session_id";
const HEARTBEAT_MS = 15_000;
const BATCH_SIZE = 10;
const BATCH_TIMEOUT_MS = 5_000;

let visitorId = "";
let sessionId = "";
let heartbeatTimer: ReturnType<typeof setInterval> | null = null;
let batchQueue: TrackingEvent[] = [];
let batchTimer: ReturnType<typeof setTimeout> | null = null;
let initialized = false;
let currentPageUrl = "";
let currentPagePath = "";
let lastPageStartTime = 0;
let maxScrollDepth = 0;
let fingerprint = "";
let currentPageTitle = "";
let isUnloadHandled = false;

interface TrackingEvent {
	visitorId: string;
	sessionId: string;
	eventType: string;
	pageUrl: string;
	pagePath: string;
	pageTitle: string;
	scrollDepth: number;
	durationMs: number;
	referrer: string;
	utmSource: string;
	utmMedium: string;
	utmCampaign: string;
	utmTerm: string;
	utmContent: string;
	deviceType: string;
	screenWidth: number;
	screenHeight: number;
	eventId: string;
	fingerprint: string;
	userId?: string;
}

/**
 * Initialize the tracker. Call once on app mount.
 */
export async function initTracker(): Promise<void> {
	if (initialized) return;
	initialized = true;
	isUnloadHandled = false;

	try {
		// 1. Generate fingerprint.
		const fp = await generateFingerprint();
		fingerprint = fp;

		// 2. Load or create visitor ID.
		visitorId = loadOrCreateVisitorId(fp);

		// 3. Load or create session ID.
		sessionId = loadOrCreateSessionId();

		// 4. Extract UTM params.

		// 5. Start heartbeat.
		startHeartbeat();

		// 6. Track visibility changes.
		document.addEventListener("visibilitychange", handleVisibilityChange);

		// 7. Track scroll depth on page leave.
		window.addEventListener("beforeunload", handleBeforeUnload);
		window.addEventListener("pagehide", handleBeforeUnload, { once: true });

		// 8. Track scroll depth updates.
		window.addEventListener("scroll", handleScroll, { passive: true });

		// 9. Send initial pageview.
		trackPageView(window.location.pathname, document.title);
	} catch (err) {
		// Graceful degradation — tracking failure should never break the app.
		console.warn("[tracker] init failed:", err);
		// Generate a fallback visitor ID so tracking continues.
		if (!visitorId) {
			visitorId = `v_fb_${crypto.randomUUID().replace(/-/g, "").slice(0, 32)}`;
			localStorage.setItem(VISITOR_ID_KEY, visitorId);
		}
		if (!sessionId) {
			sessionId = loadOrCreateSessionId();
		}
		fingerprint = "fallback";
		startHeartbeat();
		document.addEventListener("visibilitychange", handleVisibilityChange);
		window.addEventListener("beforeunload", handleBeforeUnload);
		window.addEventListener("pagehide", handleBeforeUnload, { once: true });
		window.addEventListener("scroll", handleScroll, { passive: true });
		trackPageView(window.location.pathname, document.title);
	}
}

/**
 * Record a pageview event.
 */
export function trackPageView(path: string, title: string): void {
	if (!visitorId) return;

	// Calculate time on previous page.
	if (lastPageStartTime > 0 && currentPageUrl) {
		const duration = Date.now() - lastPageStartTime;
		sendEvent({
			visitorId,
			sessionId,
			eventType: "pageview",
			pageUrl: currentPageUrl,
			pagePath: currentPagePath,
			pageTitle: currentPageTitle,
			scrollDepth: maxScrollDepth,
			durationMs: duration,
			referrer: document.referrer,
			...getUTMFields(),
			...getDeviceFields(),
			eventId: crypto.randomUUID(),
			fingerprint,
		});
	}

	// Update current page tracking.
	currentPageUrl = window.location.href;
	currentPagePath = path;
	currentPageTitle = title;
	lastPageStartTime = Date.now();
	maxScrollDepth = 0;

	// Send new pageview.
	sendEvent({
		visitorId,
		sessionId,
		eventType: "pageview",
		pageUrl: window.location.href,
		pagePath: path,
		pageTitle: title,
		scrollDepth: 0,
		durationMs: 0,
		referrer: document.referrer,
		...getUTMFields(),
		...getDeviceFields(),
		eventId: crypto.randomUUID(),
		fingerprint,
	});

	// Fire client-side pixel events (Meta, TikTok, GA4).
	fireClientSideEvents("page_view", { page_path: path, page_title: title });
}

/**
 * Record a custom event.
 */
export function trackEvent(
	name: string,
	params?: Record<string, unknown>,
): void {
	if (!visitorId) return;

	sendEvent({
		visitorId,
		sessionId,
		eventType: name,
		pageUrl: window.location.href,
		pagePath: window.location.pathname,
		pageTitle: document.title,
		scrollDepth: maxScrollDepth,
		durationMs: 0,
		referrer: document.referrer,
		...getUTMFields(),
		...getDeviceFields(),
		eventId: crypto.randomUUID(),
		fingerprint,
	});

	fireClientSideEvents(name, params);
}

/**
 * Record a conversion event (signup, purchase, etc.).
 */
export function trackConversion(
	name: string,
	params?: Record<string, unknown>,
): void {
	trackEvent(`conversion:${name}`, params);
}

/**
 * Link anonymous visitor to a logged-in user.
 */
export function identifyUser(userId: string, authToken?: string): void {
	if (!visitorId || !userId) return;
	const headers: Record<string, string> = { "Content-Type": "application/json" };
	if (authToken) {
		headers["Authorization"] = `Bearer ${authToken}`;
	}
	fetch("/api/track/identify", {
		method: "POST",
		headers,
		body: JSON.stringify({ visitorId }),
		credentials: "include",
	}).catch((e) => {
		console.error("[tracker] identifyUser failed:", e, { visitorId, userId });
	});
}

/**
 * Get the current visitor ID.
 */
export function getVisitorId(): string {
	return visitorId;
}

/**
 * Get the current session ID.
 */
export function getSessionId(): string {
	return sessionId;
}

// ── Internal helpers ──────────────────────────────────────────────────

function loadOrCreateVisitorId(fingerprint: string): string {
	// Try localStorage first.
	const stored = localStorage.getItem(VISITOR_ID_KEY);
	if (stored) return stored;

	// Generate new visitor ID from fingerprint hash.
	const id = `v_${fingerprint.slice(0, 32)}`;
	localStorage.setItem(VISITOR_ID_KEY, id);
	return id;
}

function generateSessionId(): string {
	return `s_${crypto.randomUUID().replace(/-/g, "").slice(0, 24)}`;
}

function loadOrCreateSessionId(): string {
	const stored = sessionStorage.getItem(SESSION_ID_KEY);
	if (stored) return stored;

	const id = generateSessionId();
	sessionStorage.setItem(SESSION_ID_KEY, id);
	return id;
}

function getDeviceFields() {
	const ua = navigator.userAgent;
	let deviceType = "desktop";

	// User-Agent based detection (more reliable than screen width).
	if (/iPhone|Android(?:.*Mobile)|IEMobile|BlackBerry|Opera Mini/i.test(ua)) {
		deviceType = "mobile";
	} else if (/iPad|Android(?!.*Mobile)|Tablet|PlayBook|Silk/i.test(ua)) {
		deviceType = "tablet";
	}

	// Fallback: screen-width heuristic for devices not caught by UA.
	if (deviceType === "desktop") {
		const w = window.innerWidth;
		if (w <= 768) deviceType = "mobile";
		else if (w <= 1024) deviceType = "tablet";
	}

	return {
		deviceType,
		screenWidth: window.innerWidth,
		screenHeight: window.innerHeight,
	};
}

function getUTMFields() {
	const utm = getEffectiveUTM();
	return {
		utmSource: utm.source,
		utmMedium: utm.medium,
		utmCampaign: utm.campaign,
		utmTerm: utm.term,
		utmContent: utm.content,
	};
}

function sendEvent(event: TrackingEvent): void {
	batchQueue.push(event);

	if (batchQueue.length >= BATCH_SIZE) {
		flushBatch();
	} else if (!batchTimer) {
		batchTimer = setTimeout(flushBatch, BATCH_TIMEOUT_MS);
	}
}

function flushBatch(): void {
	if (batchTimer) {
		clearTimeout(batchTimer);
		batchTimer = null;
	}

	if (batchQueue.length === 0) return;

	const events = batchQueue.splice(0, BATCH_SIZE);

	// Use batch endpoint for multiple events, single endpoint for one.
	const url = events.length > 1 ? "/api/track/batch" : "/api/track/event";
	const body = events.length > 1
		? JSON.stringify({ events })
		: JSON.stringify(events[0]);

	fetch(url, {
		method: "POST",
		headers: { "Content-Type": "application/json" },
		body,
	})
		.then((res) => res.json())
		.then((data: any) => {
			// Update session ID from server response.
			if (data?.sessionId && data.sessionId !== sessionId) {
				sessionId = data.sessionId;
				sessionStorage.setItem(SESSION_ID_KEY, sessionId);
			}
			// Update visitor ID from server response.
			if (data?.visitorId && data.visitorId !== visitorId) {
				visitorId = data.visitorId;
				localStorage.setItem(VISITOR_ID_KEY, visitorId);
			}
		})
		.catch(() => {
			// Silent failure.
		});

	// If there are more events, schedule another flush.
	if (batchQueue.length > 0) {
		batchTimer = setTimeout(flushBatch, BATCH_TIMEOUT_MS);
	}
}

function startHeartbeat(): void {
	if (heartbeatTimer) clearInterval(heartbeatTimer);

	heartbeatTimer = setInterval(() => {
		if (document.hidden) return;
		sendHeartbeat();
	}, HEARTBEAT_MS);
}

function handleVisibilityChange(): void {
	if (document.hidden) {
		// Page hidden — send final heartbeat, clear interval.
		if (heartbeatTimer) {
			clearInterval(heartbeatTimer);
			heartbeatTimer = null;
		}
		sendHeartbeat();
	} else {
		// Page visible again — restart heartbeat.
		startHeartbeat();
	}
}

function handleBeforeUnload(): void {
	if (isUnloadHandled) return;
	isUnloadHandled = true;
	// Send final pageview with duration.
	if (lastPageStartTime > 0 && currentPageUrl) {
		const duration = Date.now() - lastPageStartTime;
		const event: TrackingEvent = {
			visitorId,
			sessionId,
			eventType: "pageview",
			pageUrl: currentPageUrl,
			pagePath: currentPagePath,
			pageTitle: currentPageTitle,
			scrollDepth: maxScrollDepth,
			durationMs: duration,
			referrer: document.referrer,
			...getUTMFields(),
			...getDeviceFields(),
			eventId: crypto.randomUUID(),
			fingerprint,
		};
		if (navigator.sendBeacon) {
			navigator.sendBeacon(
				"/api/track/event",
				new Blob([JSON.stringify(event)], { type: "application/json" }),
			);
		}
	}

	// End session.
	if (sessionId) {
		if (navigator.sendBeacon) {
			navigator.sendBeacon(
				"/api/track/session/end",
				new Blob(
					[
						JSON.stringify({
							sessionId,
							exitPage: window.location.pathname,
						}),
					],
					{ type: "application/json" },
				),
			);
		}
	}
}

function handleScroll(): void {
	const scrollTop = window.scrollY || document.documentElement.scrollTop;
	const docHeight =
		document.documentElement.scrollHeight -
		document.documentElement.clientHeight;
	if (docHeight > 0) {
		const depth = Math.round((scrollTop / docHeight) * 100);
		if (depth > maxScrollDepth) {
			maxScrollDepth = depth;
		}
	}
}

function sendHeartbeat(): void {
	if (!visitorId || !sessionId) return;

	fetch("/api/track/heartbeat", {
		method: "POST",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify({
			sessionId,
			visitorId,
			pageUrl: window.location.href,
			scrollDepth: maxScrollDepth,
			durationMs: lastPageStartTime > 0 ? Date.now() - lastPageStartTime : 0,
			eventId: crypto.randomUUID(),
		}),
	})
		.then((res) => res.json())
		.then((data: any) => {
		if (data?.sessionEnded) {
			// Session was cleaned up server-side — generate a fresh ID
			// (loadOrCreateSessionId would return the old ended ID from sessionStorage).
			sessionId = generateSessionId();
			sessionStorage.setItem(SESSION_ID_KEY, sessionId);
			lastPageStartTime = 0;
			maxScrollDepth = 0;
		}
		})
		.catch(() => {});
}

/**
 * Fire client-side pixel events (Meta Pixel, TikTok Pixel, GA4 gtag).
 */
function fireClientSideEvents(
	eventName: string,
	params?: Record<string, unknown>,
): void {
	// Meta Pixel
	const fbq = (window as any).fbq;
	if (typeof fbq === "function") {
		if (eventName === "page_view") {
			fbq("track", "PageView");
		} else if (eventName === "conversion:signup") {
			fbq("track", "CompleteRegistration");
		} else {
			fbq("trackCustom", eventName, params);
		}
	}

	// TikTok Pixel
	const ttq = (window as any).ttq;
	if (typeof ttq === "function") {
		if (eventName === "page_view") {
			ttq("track", "PageView");
		} else if (eventName === "conversion:signup") {
			ttq("track", "CompleteRegistration");
		} else {
			ttq("track", "CustomEvent", { event_name: eventName });
		}
	} else if (Array.isArray(ttq)) {
		if (eventName === "page_view") {
			ttq.push(["track", "PageView"]);
		} else if (eventName === "conversion:signup") {
			ttq.push(["track", "CompleteRegistration"]);
		} else {
			ttq.push(["track", "CustomEvent", { event_name: eventName }]);
		}
	}

	// GA4 gtag
	const gtag = (window as any).gtag;
	if (typeof gtag === "function") {
		gtag("event", eventName, {
			page_path: window.location.pathname,
			...params,
		});
	}
}
